import { useTranslation } from "react-i18next";
import {
  bonusLabel,
  formatCents,
  formatPerFolio,
  packageFeatures,
  perFolioLabel,
  ribbonLabel,
  savingsPercent,
  sortCatalog,
} from "@/utils/packageFeatures.js";
import { landingFacts } from "@/utils/landingCatalog.js";
import { scrollToSection } from "./motion.js";

/** La franja de color de cada tarjeta, en el orden del catálogo. */
const COLORS = ["teal", "peach", "coral", "blue", "yellow"];

const lowerFirst = (text) => text.charAt(0).toLowerCase() + text.slice(1);

/**
 * «Entre más folios, menos pagas por cada uno»: las tarjetas con el diseño del
 * demo, pero escritas con el catálogo y sus banderas, igual que /buy_package.
 * El demo ponía la constancia en los paquetes 10 y 20 y no mencionaba la 32/D;
 * aquí cada renglón sale de la bandera del paquete. Sólo se lista lo que el
 * paquete trae: con los diez renglones, tachados incluidos, cada tarjeta medía
 * casi una pantalla.
 */
const PricingPlans = ({ packages, loading, error }) => {
  const { t } = useTranslation();
  const catalog = sortCatalog(packages);
  const { reference, bestPerFolio } = landingFacts(catalog);

  return (
    <section id="precios">
      <div className="lp-center">
        <p className="lp-eyebrow">Paquetes</p>
        <h2>Entre más folios, menos pagas por cada uno.</h2>
        <p>
          {reference && bestPerFolio && (
            <>
              El folio baja de {formatPerFolio(reference.pricePerFolioCents)} a{" "}
              {formatPerFolio(bestPerFolio.pricePerFolioCents)} conforme subes
              de paquete.{" "}
            </>
          )}
          Precios en pesos mexicanos.
        </p>
      </div>

      {loading && <p className="lp-note">{t("packages.loading")}</p>}
      {error && <p className="lp-note">{t("packages.loadError")}</p>}

      <div className="lp-pricing">
        {catalog.map((pkg, index) => {
          const previous = index > 0 ? catalog[index - 1] : null;
          const ribbon = ribbonLabel(pkg, previous, t);
          const savings = savingsPercent(pkg, catalog);
          const bonus = bonusLabel(pkg, t);
          // El que regala folios es el que el demo resalta.
          const highlight = pkg.bonusFolios > 0;
          const per = [
            perFolioLabel(pkg, t),
            bonus
              ? lowerFirst(bonus)
              : savings > 0 &&
                lowerFirst(t("packages.savings", { percent: savings })),
          ]
            .filter(Boolean)
            .join(" · ");

          return (
            <div
              className={`lp-plan lp-reveal${highlight ? " lp-hl" : ""}`}
              style={{ "--pc": `var(--${COLORS[index % COLORS.length]})` }}
              key={pkg.id}
            >
              {ribbon && <span className="lp-flag">{ribbon}</span>}
              <span className="lp-eyebrow">
                {pkg.name}
                {pkg.commercialName && ` · ${pkg.commercialName}`}
              </span>
              <div className="lp-price lp-num">
                {formatCents(pkg.priceCents)}
              </div>
              <div className="lp-per">{per}</div>
              <ul>
                {packageFeatures(pkg, previous, t)
                  .filter((feature) => feature.included)
                  .map((feature) => (
                    <li key={feature.key}>{feature.label}</li>
                  ))}
              </ul>
              <button
                type="button"
                className={`lp-btn${highlight ? "" : " lp-ghost"}`}
                onClick={() => scrollToSection("contacto")}
              >
                {t("packages.hire")}
              </button>
            </div>
          );
        })}
      </div>
      <p className="lp-fine">
        El costo por folio es referencial: resulta de dividir el precio del
        paquete entre los folios incluidos.
      </p>
    </section>
  );
};

export default PricingPlans;
